import type { KeyboardEvent } from 'react'
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
} from 'react'

import { cn, extractElementContent } from '@/utils'
import { useMergedRef } from '@mantine/hooks'

type ContentEditableProps = {
  value?: string
  onChange?: (value?: string) => Promise<void> | void
  placeholder?: string
  disabled?: boolean
  className?: string
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
}

/**
 * ContentEditable Component
 *
 * A controlled content-editable component that supports inline editing,
 * placeholder text, and event handling for confirmation or cancellation
 * of changes. It is designed to be reusable with props for customization.
 *
 * Props: - `value` (string): The current value of the editable content. -
 * `onChange` (function): Callback triggered when the content is confirmed to
 * have changed. - `placeholder` (string): Placeholder text shown when the
 * content is empty. - `disabled` (bool): If true, the component renders as
 * non-editable text. - `className` (string): Additional CSS classes for styling
 * the component.
 *
 * Features: - Supports Enter (to confirm changes) and Escape (to cancel
 * changes) keyboard shortcuts. - Supports Shift + Enter to add a new line. -
 * Implements true optimistic updates - UI maintains user's edit during the
 * entire onChange process. - Preserves empty lines at the end of content. -
 * Falls back to original content only on error. - Allows for graceful fallback
 * to a non-editable paragraph when disabled.
 */
export const ContentEditable = forwardRef<HTMLDivElement, ContentEditableProps>(
  (
    {
      value: defaultValue = '',
      onChange,
      placeholder = '',
      disabled = false,
      className = '',
      onKeyDown: externalOnKeyDown,
    },
    forwardedRef,
  ) => {
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState(() => defaultValue)

    const editorRef = useRef<HTMLDivElement>(null)
    const mergedRef = useMergedRef(editorRef, forwardedRef)

    const isEmpty = value.trim().length === 0

    const onConfirm = useCallback(() => {
      if (!editorRef.current) return

      const newValue = extractElementContent(editorRef.current)

      if (defaultValue === newValue) {
        return
      }

      setValue(newValue)
      void onChange?.(newValue)
    }, [defaultValue, onChange])

    const handleBlur = useCallback(() => {
      onConfirm()
      setEditing(false)
    }, [onConfirm])

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      externalOnKeyDown?.(event as KeyboardEvent<HTMLDivElement>)

      if (event.defaultPrevented) return

      if (event.key === 'Enter') {
        // Allow Shift + Enter to insert a new line
        if (event.shiftKey) return

        event.preventDefault()
        onConfirm()
      } else if (event.key === 'Escape') {
        event.preventDefault()
      }
    }

    function handleFocus() {
      setEditing(true)
    }

    // Update optimistic value when the external value changes
    useLayoutEffect(() => {
      if (!editing && value !== defaultValue) {
        setValue(defaultValue)
      }

      // We don't need to subscribe to anything else, except for defaultValue.
      // Since this hook is for sync the external state (primarily from realtime listener).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue])

    if (disabled) {
      const lines = value.split('\n')

      return (
        <p className={cn('px-1 text-left break-words', className)}>
          {/* For disabled state, show newlines as line breaks */}
          {lines.map((line, i) => (
            <span key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      )
    }

    return (
      <div className="relative w-full">
        <div
          ref={mergedRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          className={cn(
            'px-1 w-full break-words bg-transparent rounded-sm text-left cursor-text',
            'focus:outline-hidden focus:ring-3 focus:ring-blue-500/50',
            // loading ? 'bg-animated from-gray-800/50 to-gray-600/50' : '',
            'transition-colors ease-in-out duration-300',
            !disabled && 'hover:bg-gray-600/40 focus:bg-transparent',
            'whitespace-pre-wrap', // This makes sure that line breaks show up
            className,
          )}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={handleFocus}
          onFocus={handleFocus}
        >
          {value}
        </div>

        {isEmpty && !editing && (
          <span className="absolute top-0 left-0 px-1 text-white/50 pointer-events-none hover:opacity-100 group-hover:opacity-100 opacity-0 transition-opacity">
            {placeholder}
          </span>
        )}
      </div>
    )
  },
)

ContentEditable.displayName = 'ContentEditable'
